import {
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnFiltersState,
    type SortingState,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import type { BulkAction, DataTableColumn, UseDataTableReturn } from '../types';

/**
 * Props for the useDataTable hook
 */
interface UseDataTableProps<TData> {
  data: TData[];
  columns: DataTableColumn<TData>[];
  getRowId?: (row: TData) => string;
  enableSelection?: boolean;
  bulkActions?: BulkAction<TData>[];
  initialSorting?: SortingState;
  onSortChange?: (sorting: SortingState) => void;
  manualSorting?: boolean;
  manualPagination?: boolean;
}

/**
 * Enhanced useDataTable hook that provides all table functionality
 */
export function useDataTable<TData>({
  data,
  columns,
  getRowId,
  enableSelection = false,
  bulkActions = [],
  initialSorting = [],
  onSortChange,
  manualSorting = false,
  manualPagination = false,
}: UseDataTableProps<TData>): UseDataTableReturn<TData> {
  
  // Table state
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');

  // Handle sorting changes
  const handleSortingChange = useCallback((updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortChange) {
      onSortChange(newSorting);
    }
  }, [sorting, onSortChange]);

  // Prepare columns - selection column will be added in the DataTable component
  const tableColumns = useMemo(() => {
    return columns;
  }, [columns]);

  // Create table instance
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: enableSelection,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    manualSorting,
    manualPagination,
  });

  // Get selected items
  const selectedItems = useMemo(() => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    return selectedRows.map(row => row.original);
  }, [table, rowSelection]);

  // Selection helpers
  const selection = useMemo(() => ({
    selectAll: () => table.toggleAllPageRowsSelected(),
    clearSelection: () => setRowSelection({}),
    toggleItem: (item: TData) => {
      const id = getRowId ? getRowId(item) : String(data.indexOf(item));
      setRowSelection(prev => ({
        ...prev,
        [id]: !prev[id],
      }));
    },
    isSelected: (item: TData) => {
      const id = getRowId ? getRowId(item) : String(data.indexOf(item));
      return !!rowSelection[id];
    },
    isAllSelected: table.getIsAllPageRowsSelected(),
    isIndeterminate: table.getIsSomePageRowsSelected(),
  }), [table, rowSelection, getRowId, data]);

  // Search helpers
  const search = useMemo(() => ({
    query: (query: string) => {
      setSearchQuery(query);
      // Apply global filter
      table.setGlobalFilter(query);
    },
    clear: () => {
      setSearchQuery('');
      table.setGlobalFilter('');
    },
  }), [table]);

  // Bulk action helpers
  const bulkActionHelpers = useMemo(() => ({
    execute: async (actionId: string) => {
      const action = bulkActions.find(a => a.id === actionId);
      if (action && selectedItems.length > 0) {
        await action.handler(selectedItems);
        // Clear selection after action
        setRowSelection({});
      }
    },
    canExecute: (actionId: string) => {
      const action = bulkActions.find(a => a.id === actionId);
      return !!action && !action.disabled && selectedItems.length > 0;
    },
  }), [bulkActions, selectedItems]);

  return {
    table,
    selectedItems,
    selection,
    searchQuery,
    search,
    bulkActions: bulkActionHelpers,
  };
} 